import { useCallback, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

/**
 * Bottom Sheet am Griff nach unten ziehen und damit schliessen.
 *
 * Der Griff oben am Kartendetail sah von Anfang an ziehbar aus — ein
 * Versprechen, das die Oberflaeche gemacht und nicht eingeloest hat. Das hier
 * loest es ein.
 *
 * Die Geste haengt bewusst NUR am Griff, nicht am ganzen Sheet: der Inhalt
 * scrollt. Laege sie darueber, konkurrierte jedes Wischen im Formular mit dem
 * Schliessen — genau das macht Bottom Sheets anderswo unbenutzbar. Der Griff
 * ist `sticky top-0` und damit auch weit unten noch erreichbar.
 *
 * Pointer-Events statt Touch-Events: derselbe Weg gilt fuer Finger, Maus und
 * Stift, und `setPointerCapture` haelt die Bewegung auch dann fest, wenn der
 * Finger den Griff verlaesst — was er beim Ziehen zwangslaeufig tut.
 */

/** Ab welchem Anteil der Sheet-Hoehe losgelassen wird geschlossen. */
const DISMISS_FRACTION = 0.25;

/**
 * Ab welcher Geschwindigkeit (Pixel je Millisekunde) auch ein kurzer Zug
 * schliesst. Ein schneller Wisch ueber 60 px ist eindeutig gemeint, auch wenn
 * er das Viertel nicht erreicht.
 */
const DISMISS_VELOCITY = 0.5;

/** Darunter gilt es als Zupfer und nicht als Zug. */
const MIN_DISTANCE = 40;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

export interface SheetDrag {
  /** An das Sheet selbst — der Knoten, der sich bewegt. */
  sheetRef: RefObject<HTMLDivElement | null>;
  /** An den Ziehgriff. */
  handleProps: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
    style: { touchAction: 'none' };
  };
}

export function useSheetDrag(onDismiss: () => void): SheetDrag {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const start = useRef<{ y: number; at: number } | null>(null);

  /*
   * Die Verschiebung geht direkt an den Knoten, nicht durch React. Ein
   * `setState` je `pointermove` wuerde das ganze Sheet — Kommentare,
   * Checkliste, Anhaenge — bei jedem Pixel neu rendern, und die Geste
   * ruckelte genau auf den Geraeten, fuer die sie gedacht ist.
   */
  const move = useCallback((dy: number, animated: boolean) => {
    const node = sheetRef.current;
    if (!node) return;
    node.style.transition = animated && !prefersReducedMotion() ? 'transform 150ms ease-out' : '';
    node.style.transform = dy > 0 ? `translateY(${dy}px)` : '';
  }, []);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    // Nur der Hauptzeiger: ein zweiter Finger soll die Geste nicht kapern.
    if (!event.isPrimary) return;
    start.current = { y: event.clientY, at: event.timeStamp };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!start.current) return;
      // Nur nach unten. Nach oben zu ziehen hiesse, das Sheet ueber den
      // Bildschirmrand zu schieben — dort ist nichts.
      move(Math.max(0, event.clientY - start.current.y), false);
    },
    [move],
  );

  const finish = useCallback(
    (event: ReactPointerEvent<HTMLElement>, cancelled: boolean) => {
      const from = start.current;
      start.current = null;
      if (!from) return;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      const dy = event.clientY - from.y;
      const elapsed = Math.max(1, event.timeStamp - from.at);
      const height = sheetRef.current?.getBoundingClientRect().height ?? 0;

      const weitGenug = dy > height * DISMISS_FRACTION;
      const schnellGenug = dy > MIN_DISTANCE && dy / elapsed > DISMISS_VELOCITY;

      if (!cancelled && (weitGenug || schnellGenug)) {
        onDismiss();
        return;
      }
      // Zurueckfedern. Der Knoten bleibt stehen, das Sheet wird nicht
      // geschlossen — ein Zupfer soll nichts kosten.
      move(0, true);
    },
    [move, onDismiss],
  );

  return {
    sheetRef,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: (event) => finish(event, false),
      onPointerCancel: (event) => finish(event, true),
      // Ohne das scrollt der Browser die Seite mit, statt die Geste
      // durchzureichen.
      style: { touchAction: 'none' },
    },
  };
}
